// Bowel catch-up review modal (CATCHUP_WIDGETS_PLAN.md Task 7). Lets the user mark
// each gap day from the past week "Log it" (Bristol scale + editable time,
// default noon per D-CU-9) or "Didn't go" (a confirmed no-BM day, D-CU-4). Direct
// save, no pending/confirm, no Bedrock -- POST /catch-up/log. Copies the
// MissedDoseBackfill.tsx shell (staged rows, escape-to-close, local-date->ISO).

import { useState } from "react";
import type { CatchUpLogEntry } from "@medassist/shared";
import { formatCalendarDate, bristolLabel } from "@medassist/shared";
import { logCatchUpEntries, ApiError } from "../lib/api.js";
import { invalidateCatchUp } from "../lib/catchUpFetch.js";
import { ModalShell, CHOICE_CLASS } from "./ModalShell.js";
import { PoopIcon } from "./icons.js";
import { ClearableInput } from "./ClearableInput.js";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function todayStr(): string {
  return localDateStr(new Date());
}

function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDateStr(d);
}

function dayHeading(dayKey: string): string {
  if (dayKey === todayStr()) return "Today";
  if (dayKey === daysAgoStr(1)) return "Yesterday";
  return formatCalendarDate(dayKey, { weekday: "short", month: "short", day: "numeric" });
}

function dateTimeToIso(date: string, time: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const [h, m] = time.split(":").map(Number);
  return new Date(year!, month! - 1, day!, h ?? 12, m ?? 0, 0, 0).toISOString();
}

function to12Hour(hm: string): string {
  const [hStr, mStr] = hm.split(":");
  let h = Number(hStr);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
}

const BRISTOL_SCALES = [1, 2, 3, 4, 5, 6, 7];

interface StagedLogged {
  action: "logged";
  time: string; // HH:MM local, default noon (D-CU-9)
  bristolScale: number | null;
  notes: string;
}
interface StagedNone {
  action: "none";
}
type Staged = StagedLogged | StagedNone;

/** Matches api.ts's catchUpEntryKey for bowel entries. */
function entryKeyFor(dayKey: string): string {
  return `bowel|${dayKey}|`;
}

export function BowelCatchUpModal({
  patientId,
  gapDays,
  onClose,
  onLogged,
}: {
  patientId: string;
  gapDays: string[];
  onClose: () => void;
  /** Called after at least one entry saves so the widget can refetch + toast. */
  onLogged: (count: number) => void;
}) {
  const [staged, setStaged] = useState<Map<string, Staged>>(new Map());
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [settled, setSettled] = useState<Set<string>>(new Set());
  const [skipReasons, setSkipReasons] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visible = gapDays.filter((d) => !settled.has(d));

  function stageLog(dayKey: string) {
    setStaged((prev) =>
      new Map(prev).set(dayKey, { action: "logged", time: "12:00", bristolScale: null, notes: "" })
    );
    setEditingDay(dayKey);
  }

  function stageNone(dayKey: string) {
    setStaged((prev) => new Map(prev).set(dayKey, { action: "none" }));
    if (editingDay === dayKey) setEditingDay(null);
  }

  function unstage(dayKey: string) {
    setStaged((prev) => {
      const next = new Map(prev);
      next.delete(dayKey);
      return next;
    });
    if (editingDay === dayKey) setEditingDay(null);
  }

  function updateLogged(dayKey: string, patch: Partial<StagedLogged>) {
    setStaged((prev) => {
      const existing = prev.get(dayKey);
      if (!existing || existing.action !== "logged") return prev;
      const next = new Map(prev);
      next.set(dayKey, { ...existing, ...patch });
      return next;
    });
  }

  async function save() {
    if (staged.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const entries: CatchUpLogEntry[] = [...staged.entries()].map(([dayKey, action]) => {
        if (action.action === "none") {
          return { kind: "bowel", dayKey, action: "none" };
        }
        return {
          kind: "bowel",
          dayKey,
          action: "logged",
          occurredAt: dateTimeToIso(dayKey, action.time),
          ...(action.bristolScale !== null ? { bristolScale: action.bristolScale } : {}),
          ...(action.notes.trim() ? { notes: action.notes.trim() } : {}),
        };
      });
      const { saved, skipped } = await logCatchUpEntries(patientId, entries);
      const nowSettled = new Set(settled);
      const reasons = new Map(skipReasons);
      const stagedDays = [...staged.keys()];
      for (const s of saved) {
        const dayKey = stagedDays.find((d) => entryKeyFor(d) === s.entryKey);
        if (dayKey) nowSettled.add(dayKey);
      }
      for (const s of skipped) {
        const dayKey = stagedDays.find((d) => entryKeyFor(d) === s.entryKey);
        if (dayKey) {
          nowSettled.add(dayKey);
          reasons.set(dayKey, s.reason);
        }
      }
      setSettled(nowSettled);
      setSkipReasons(reasons);
      setStaged(new Map());
      setEditingDay(null);
      invalidateCatchUp(patientId);
      if (saved.length > 0) onLogged(saved.length);
      if (skipped.length === 0) {
        onClose();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  }

  const nothingLeft = visible.length === 0 && skipReasons.size === 0;

  function renderDay(dayKey: string) {
    const action = staged.get(dayKey);
    const isEditing = editingDay === dayKey && action?.action === "logged";

    return (
      <li key={dayKey} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="flex-1 font-medium text-gray-900 dark:text-gray-100">{dayHeading(dayKey)}</span>
          {!action && (
            <div className="flex shrink-0 gap-1.5">
              <button onClick={() => stageLog(dayKey)} className={CHOICE_CLASS}>
                Log it
              </button>
              <button onClick={() => stageNone(dayKey)} className={CHOICE_CLASS}>
                Didn&apos;t go
              </button>
            </div>
          )}
        </div>

        {action?.action === "none" && (
          <div className="mt-1.5 flex items-center gap-2 text-sm">
            <span className="flex-1 text-gray-500 dark:text-gray-400">Marked as no bowel movement</span>
            <button
              onClick={() => unstage(dayKey)}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              Undo
            </button>
          </div>
        )}

        {action?.action === "logged" && !isEditing && (
          <div className="mt-1.5 flex items-center gap-2 text-sm">
            <span className="flex-1 text-gray-500 dark:text-gray-400">
              Logged at {to12Hour(action.time)}
              {action.bristolScale !== null ? ` — ${bristolLabel(action.bristolScale)}` : ""}
            </span>
            <button
              onClick={() => setEditingDay(dayKey)}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-(--accent) hover:bg-(--accent)/10"
            >
              Edit
            </button>
            <button
              onClick={() => unstage(dayKey)}
              className="rounded-md px-2.5 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              Undo
            </button>
          </div>
        )}

        {isEditing && action?.action === "logged" && (
          <div className="mt-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="time"
                value={action.time}
                onChange={(e) => updateLogged(dayKey, { time: e.target.value })}
                className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
              />
              <ClearableInput
                type="text"
                value={action.notes}
                onChange={(e) => updateLogged(dayKey, { notes: e.target.value })}
                placeholder="Notes (optional)"
                className="min-w-0 flex-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="grid grid-cols-7 gap-1">
              {BRISTOL_SCALES.map((scale) => {
                const selected = action.bristolScale === scale;
                return (
                  <button
                    key={scale}
                    onClick={() => updateLogged(dayKey, { bristolScale: selected ? null : scale })}
                    title={bristolLabel(scale)}
                    className={`rounded-md border py-1 text-xs ${
                      selected
                        ? "border-(--accent) bg-(--accent)/10 font-semibold text-(--accent)"
                        : "border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  >
                    {scale}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setEditingDay(null)}
              className="rounded-md bg-(--accent) px-2.5 py-1 text-xs font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
            >
              Done
            </button>
          </div>
        )}
      </li>
    );
  }

  return (
    <ModalShell
      title="Catch up on bowel movements"
      icon={<PoopIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      footer={
        !nothingLeft && (
          <button
            onClick={() => void save()}
            disabled={busy || staged.size === 0}
            className="w-full rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50"
          >
            {busy ? "Saving…" : `Save ${staged.size} ${staged.size === 1 ? "day" : "days"}`}
          </button>
        )
      }
    >
      {error && (
        <p className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </p>
      )}

      {[...skipReasons.entries()].map(([dayKey, reason]) => (
        <p
          key={dayKey}
          className="mb-2 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
        >
          {dayHeading(dayKey)}: {reason}
        </p>
      ))}

      {nothingLeft ? (
        <p className="py-6 text-center text-sm text-gray-600 dark:text-gray-300">All caught up ✓</p>
      ) : (
        <ul className="space-y-2">{visible.map((d) => renderDay(d))}</ul>
      )}
    </ModalShell>
  );
}
